import { Observable, of as observableOf } from 'rxjs';

import { map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { RequestService } from './requestService';

import { UserProfile } from '../classes/userProfile';
import { User } from '../classes/user';
import { Employment } from '../classes/employment';
import { EmploymentInvite } from '../classes/invite';
import { Page } from '../classes/page';
import { environment } from '../../../environments/environment';
import { UnitRoom } from '../classes/unitRoom';
import { Unit } from '../../settings-module/models/unit';

@Injectable()
export class UnitService {
  constructor(
    private http: HttpClient,
    private _requestService: RequestService
  ) {}

  private _apiEndpoint = environment.apiUrl + 'units/'; // URL to web api

  public invalidatePagination(path: string) {
    this._requestService.setPaginationPath(path, null);
  }

  public getUnit(id: number) {
    return this._requestService
      .request<Unit>('GET', this._apiEndpoint + id + '/', 'unit')
      .pipe(
        tap(res => {
          (<any>window).dataLayer.push({
            unitName: res.name,
          });
        })
      );
  }

  public getUnitTree(id: number) {
    return this._requestService
      .request<Unit>('GET', this._apiEndpoint + id + '/tree', 'unit')
      .pipe(
        tap(res => {
          (<any>window).dataLayer.push({
            unitName: res.name,
          });
        })
      );
  }

  public updateUnit(unit: Unit) {
    // Invalidates the cached key every time the unit is updated
    this._requestService.invalidate('unit');
    return this._requestService.request<Unit>(
      'PUT',
      this._apiEndpoint + unit.id + '/',
      null,
      unit
    );
  }

  public deleteUnit(id: number) {
    return this.http.delete(this._apiEndpoint + id + '/');
  }

  public register(unit: Unit) {
    return this.http.post<Unit>(this._apiEndpoint + 'registration/', unit);
  }

  public createEmploymentForInvite(
    unit_id: number,
    position: string,
    user: User
  ) {
    user.user_profile = <UserProfile>{};
    const body = JSON.stringify({
      employment: {
        unit: unit_id,
        position: position,
        user: String(user.id),
      },
    });
    return this.http.post<Employment>(
      this._apiEndpoint + unit_id + '/employments/',
      body
    );
  }

  public getEmployments(unit_id: number) {
    return this.http.get<Page<Employment>>(
      this._apiEndpoint + unit_id + '/employments/?page_size=100'
    );
  }

  public getUnitRooms(
    usePagination: boolean = false,
    statusFilter: number = null,
    jobId: number = null,
    clear: boolean = false,
    search: string = ''
  ): Observable<UnitRoom[]> {
    const key = 'unit-rooms';

    let url = this._requestService.getPaginationPath(key);

    if (clear) {
      this._requestService.clearPaginationPath(key);
    }

    // Tries to retrieve the next url to call

    if (url === 'empty') {
      return observableOf();
    }

    // If it does not exist we use the original
    if (url == null) {
      url =
        environment.apiUrl +
        'units/' +
        localStorage.getItem('unit_id') +
        '/unit-rooms/current-employee/';

      let par = '';

      if (usePagination) {
        par += (par ? '&' : '?') + 'use_pagination=True';
      }
      if (statusFilter != null) {
        par += (par ? '&' : '?') + 'status_filter=' + statusFilter;
      }
      if (jobId != null) {
        par += (par ? '&' : '?') + 'job_id=' + jobId;
      }
      if (search) {
        par += (par ? '&' : '?') + 'applicant_filter=' + search;
      }

      url += par;
    }

    if (usePagination) {
      // Calls the request service to perform request
      return this._requestService.request<Page<UnitRoom>>('GET', url).pipe(
        tap(res => {
          let next = res.next;

          if (next == null) {
            next = 'empty';
          }

          this._requestService.setPaginationPath(key, next);
        }),
        map(res => res.results)
      );
    }

    let params = '';

    if (statusFilter != null) {
      params += (params ? '&' : '?') + 'status_filter=' + statusFilter;
    }
    if (jobId != null) {
      params += (params ? '&' : '?') + 'job_id=' + jobId;
    }
    if (search) {
      params += (params ? '&' : '?') + 'applicant_filter=' + search;
    }

    return this._requestService.request<UnitRoom[]>(
      'GET',
      environment.apiUrl +
        'units/' +
        localStorage.getItem('unit_id') +
        '/unit-rooms/current-employee/' +
        params
    );
  }

  public resendInviteMail(
    email: string,
    employment_id: number,
    unit_id: number
  ) {
    const body = JSON.stringify({ unit_id: unit_id, email: email });
    return this.http.post<EmploymentInvite>(
      environment.apiUrl +
        'units/' +
        unit_id +
        '/employments/' +
        employment_id +
        '/resend-invitation/',
      body
    );
  }
}
